import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";
import { Badge } from "@/components/ui";
import { cn, toApiSort } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  downloadBulkUploadErrorLog,
  downloadBulkUploadFile,
  getBulkUploadAuditRows,
  useGetBulkUploadAuditRows,
  useGetBulkUploadHistory,
} from "@/services";
import { auditRowsToCsv, formatDateTime, rowStatusMeta, saveBlob } from "../consts";

const errorToast = (message: string) => toast.error(message);

const useUploadDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { attachmentId } = useParams();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const communityId = communityData?.communityId?.toString() as string;

  const [globalFilter, setGlobalFilter] = useState("");
  const [rowFilter, setRowFilter] = useState("ALL");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState<any>([{ id: "attachmentRowNumber", desc: false }]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const historyPath = location.pathname.replace(/\/[^/]+\/?$/, "");
  const usersPath = location.pathname.split(`/${routes.uploadHistory}`)[0];
  const usersGridPath = `${usersPath}?communityId=${communityId ?? ""}`;

  // Upload header: the same history row the list shows
  const { data: uploadData, isLoading: isUploadLoading } = useGetBulkUploadHistory({
    communityId,
    queryKey: [attachmentId],
    data: {
      filters: [{ field: "attachmentId", value: Number(attachmentId), operator: "EQUAL" }],
      pageNumber: 0,
      pageSize: 1,
    },
    queryParams: { enabled: !!communityId && !!attachmentId },
  });
  const upload = uploadData?.data?.[0];
  const isCommitted = upload?.status === "COMMITTED";

  const rowFilters = [
    { field: "attachmentId", value: Number(attachmentId), operator: "EQUAL" },
    ...(rowFilter === "ALL" ? []
      : rowFilter === "WARNING" ? [{ field: "hasWarning", value: true, operator: "EQUAL" }]
      : [{ field: "status", value: rowFilter, operator: "EQUAL" }]),
  ];

  const { data: rowsData, isLoading: isRowsLoading, refetch: refetchRows } = useGetBulkUploadAuditRows({
    communityId,
    queryKey: [attachmentId],
    data: {
      filters: rowFilters,
      search: globalFilter,
      searchFields: ["userFullName", "propertyName", "propertyDisplayId", "message"],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "attachmentRowNumber", direction: "ASC" }),
    },
    queryParams: { enabled: !!communityId && isCommitted },
  });

  useEffect(() => {
    if (isCommitted) refetchRows();
  }, [pageNumber, pageSize, sorting, rowFilter]);

  useEffect(() => {
    if (isCommitted && (globalFilter?.length >= 2 || globalFilter?.length == 0)) {
      setPageNumber(0);
      refetchRows();
    }
  }, [globalFilter]);

  const onRowFilterChange = (value: string) => {
    setPageNumber(0);
    setRowFilter(value);
  };

  const onOpenUser = (userId: any) => navigate(`${usersPath}/${routes.details}/${userId}`);

  const run = async (key: string, fn: () => Promise<void>, failMessage: string) => {
    setDownloading(key);
    try {
      await fn();
    } catch {
      errorToast(failMessage);
    } finally {
      setDownloading(null);
    }
  };

  const onDownloadFile = () =>
    run("file", async () => {
      const res = await downloadBulkUploadFile(attachmentId as string);
      saveBlob(res.data, upload?.fileName || `owner-upload-${attachmentId}.xlsx`);
    }, "Could not download the uploaded file.");

  const onDownloadErrorLog = () =>
    run("errors", async () => {
      const res = await downloadBulkUploadErrorLog(communityId, attachmentId as string);
      saveBlob(res.data, `owner-upload-${attachmentId}-errors.csv`);
    }, "Could not download the error log.");

  // Every row of this upload (not just the visible page), outcome + original Excel values
  const onDownloadAudit = () =>
    run("audit", async () => {
      const res = await getBulkUploadAuditRows(communityId, {
        filters: [{ field: "attachmentId", value: Number(attachmentId), operator: "EQUAL" }],
        pageNumber: 0,
        pageSize: 10000,
        sort: [{ field: "attachmentRowNumber", direction: "ASC" }],
      });
      const csv = auditRowsToCsv(res?.data?.data ?? []);
      saveBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `owner-upload-${attachmentId}-audit.csv`);
    }, "Could not download the audit.");

  const count = (value: any) => (value ? Number(value) : 0);

  const summaryCards = [
    { title: "Total Rows", count: count(upload?.totalRows), textClassName: "text-primary", className: "bg-primary" },
    { title: "New Users", count: count(upload?.newUserCount), textClassName: "text-green-600", className: "bg-green-600" },
    { title: "Linked to Existing", count: count(upload?.mergedCount), textClassName: "text-blue-600", className: "bg-blue-600" },
    { title: "Skipped", count: count(upload?.skippedCount), textClassName: "text-gray-600", className: "bg-gray-500" },
    { title: "Errors", count: count(upload?.errorCount), textClassName: "text-red-600", className: "bg-red-600" },
  ];

  const timelineItems = [
    {
      id: 1,
      name: "Uploaded and validated",
      desc: `${formatDateTime(upload?.uploadedAt)}${upload?.uploadedByName ? ` by ${upload.uploadedByName}` : ""} · ${upload?.fileName ?? ""}`,
      isActive: true,
    },
    isCommitted
      ? {
          id: 2,
          name: "Imported",
          desc: `${formatDateTime(upload?.committedAt)}${upload?.committedByName ? ` by ${upload.committedByName}` : ""} · `
            + `${count(upload?.newUserCount)} new, ${count(upload?.mergedCount)} linked, `
            + `${count(upload?.skippedCount)} skipped, ${count(upload?.errorCount)} errors`
            + (count(upload?.warningCount) ? `, ${count(upload?.warningCount)} with warnings` : ""),
          isActive: true,
        }
      : {
          id: 2,
          name: "Not imported",
          desc: "The file was validated but never committed, so no users were created or linked.",
          isActive: false,
        },
  ];

  const columns = [
    {
      accessorKey: "attachmentRowNumber",
      header: "Row #",
      accessorFn: (row: any) => String(row?.attachmentRowNumber ?? ""),
    },
    {
      accessorKey: "userFullName",
      header: "User",
      cell: ({ row }: any) =>
        row.original?.userId
          ? <button
              type="button"
              className="text-primary hover:underline text-left"
              onClick={(e) => { e.stopPropagation(); onOpenUser(row.original.userId); }}
            >
              {row.original?.userFullName || `User ${row.original.userId}`}
            </button>
          : <span className="text-gray-400">-</span>,
    },
    {
      accessorKey: "propertyName",
      header: "Property",
      accessorFn: (row: any) =>
        row?.propertyName ? `${row.propertyName}${row?.propertyDisplayId ? ` (${row.propertyDisplayId})` : ""}` : "-",
    },
    {
      accessorKey: "status",
      header: "Outcome",
      cell: ({ row }: any) => {
        const meta = rowStatusMeta[row.original?.status];
        return (
          <div className="flex flex-row items-center gap-1">
            <Badge className={cn("rounded-full", meta?.className)}>{meta?.label ?? row.original?.status}</Badge>
            {row.original?.hasWarning ? <Badge className="rounded-full bg-yellow-100 text-yellow-700">Warning</Badge> : <></>}
          </div>
        );
      },
    },
    {
      accessorKey: "message",
      header: "Message",
      cell: ({ row }: any) => (
        <span className="line-clamp-2 max-w-[420px] whitespace-normal">{row.original?.message || "-"}</span>
      ),
    },
  ];

  const breadcrumbData = [
    { link: "", name: "Master Data" },
    { link: usersGridPath, name: `Manage Users - ${communityData?.name ?? ""}` },
    { link: historyPath, name: "Upload History" },
    { link: "", name: <span className="text-primary">{upload?.fileName ?? "Upload"}</span> },
  ];

  return {
    upload,
    isCommitted,
    isUploadLoading,
    breadcrumbData,
    summaryCards,
    timelineItems,
    columns,
    rowsData,
    isRowsLoading,
    globalFilter,
    setGlobalFilter,
    rowFilter,
    onRowFilterChange,
    pageNumber,
    pageSize,
    setPageNumber,
    setPageSize,
    sorting,
    setSorting,
    selectedRow,
    setSelectedRow,
    onOpenUser,
    downloading,
    onDownloadFile,
    onDownloadErrorLog,
    onDownloadAudit,
  };
};

export default useUploadDetail;
